import Papa from 'papaparse';
import { z } from 'zod';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api.js';
import { type ParsedProspectData, type ImportError } from '@/pages/Import';
import { PIPELINE_STAGES } from '@/lib/constants';

// Schema for validating CSV row data
const csvRowSchema = z.object({
  company: z.string().min(1, 'Company name is required'),
  contactName: z.string().min(1, 'Contact name is required'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  stage: z.string().optional(),
  notes: z.string().optional(),
});

export interface CsvParseResult {
  data: ParsedProspectData[];
  errors: ImportError[];
}

export function useCsvImport() {
  const bulkCreateProspects = useMutation(api.prospects.bulkCreateProspects);

  const parseCSV = async (file: File): Promise<CsvParseResult> => {
    return new Promise((resolve) => {
      const data: ParsedProspectData[] = [];
      const errors: ImportError[] = [];
      const seenEmails = new Set<string>();

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transform: (value) => {
          // Trim whitespace from all fields
          return typeof value === 'string' ? value.trim() : value;
        },
        complete: (results) => {
          // Check for required headers
          const headers = results.meta.fields || [];
          const requiredHeaders = ['company', 'contactName', 'email'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          
          if (missingHeaders.length > 0) {
            errors.push({
              row: 0,
              message: `Missing required columns: ${missingHeaders.join(', ')}`,
            });
            resolve({ data, errors });
            return;
          }

          // Process each row
          results.data.forEach((row: unknown, index: number) => {
            const rowNumber = index + 1; // 1-based for user display

            try {
              // Validate the row data
              const validatedRow = csvRowSchema.parse(row);
              
              // Check for duplicate emails
              const email = validatedRow.email.toLowerCase();
              if (seenEmails.has(email)) {
                errors.push({
                  row: rowNumber,
                  field: 'email',
                  message: 'Duplicate email address',
                  data: validatedRow,
                });
                return;
              }
              seenEmails.add(email);

              // Validate stage if provided
              let stage = validatedRow.stage?.toLowerCase();
              if (stage && !PIPELINE_STAGES.some(s => s.value === stage)) {
                errors.push({
                  row: rowNumber,
                  field: 'stage',
                  message: `Invalid stage "${validatedRow.stage}". Must be one of: ${PIPELINE_STAGES.map(s => s.value).join(', ')}`,
                  data: validatedRow,
                });
                stage = 'new'; // Default to 'new' for invalid stages
              }

              // Add to data array
              data.push({
                company: validatedRow.company,
                contactName: validatedRow.contactName,
                email: validatedRow.email,
                phone: validatedRow.phone,
                stage: stage || 'new',
                notes: validatedRow.notes,
              });

            } catch (error) {
              if (error instanceof z.ZodError) {
                // Handle validation errors
                error.issues.forEach(err => {
                  errors.push({
                    row: rowNumber,
                    field: err.path[0]?.toString(),
                    message: err.message,
                    data: row as ParsedProspectData | undefined,
                  });
                });
              } else {
                errors.push({
                  row: rowNumber,
                  message: 'Failed to parse row',
                  data: row as ParsedProspectData | undefined,
                });
              }
            }
          });

          resolve({ data, errors });
        },
        error: (error) => {
          errors.push({
            row: 0,
            message: `CSV parsing error: ${error.message}`,
          });
          resolve({ data, errors });
        },
      });
    });
  };

  const importProspects = async (prospects: ParsedProspectData[]) => {
    try {
      // Convert data to the format expected by Convex
      const convexProspects = prospects.map(prospect => ({
        company: prospect.company,
        contactName: prospect.contactName,
        email: prospect.email,
        phone: prospect.phone || undefined,
        stage: prospect.stage as 'new' | 'contacted' | 'qualified' | 'interested' | 'committed' | 'participant',
        notes: prospect.notes || undefined,
      }));

      const prospectIds = await bulkCreateProspects({ prospects: convexProspects });
      
      return {
        successful: prospectIds.length,
        failed: 0,
        errors: [],
        createdProspects: prospectIds,
      };
    } catch (error) {
      // Log error details in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Import error:', error);
      }
      throw new Error(error instanceof Error ? error.message : 'Import failed');
    }
  };

  return {
    parseCSV,
    importProspects,
  };
}